import type { CaseManifestV1 } from "@cold-case/shared/case-manifest";
import {
  buildingDisplayNameForId,
  buildingLeadDescription,
  compactAssetStyleBuildingLabel,
  looksLikeAssetSlugBuildingLabel,
  normalizeManifestBuildings,
  normalizeManifestClues,
} from "@cold-case/shared/manifest-display";

export const VACANT_PROPERTY_LABEL = "A Vacant Property";

export { buildingLeadDescription };

/** Session boot: coerce clue titles and compact asset-slug building names. */
export function prepareClientCaseManifest(manifest: CaseManifestV1): CaseManifestV1 {
  return normalizeManifestBuildings(normalizeManifestClues(manifest));
}

/**
 * Player-facing building title — use for HUD, map, investigation modals, timeline, radio.
 * Never read `building.display_name` directly in UI.
 */
export function resolveBuildingDisplayName(
  manifest: CaseManifestV1,
  placementId: string,
  opts?: { decor?: boolean; assetLabel?: string }
): string {
  if (opts?.decor) return VACANT_PROPERTY_LABEL;
  const b = manifest.buildings.find((x) => x.building_id === placementId);
  if (!b) return VACANT_PROPERTY_LABEL;
  return buildingDisplayNameForId(manifest, placementId, {
    assetLabel: opts?.assetLabel,
  });
}

/** Title for overlays when only free-form copy is available (e.g. follower mirror). */
export function resolveBuildingTitleFromCopy(
  manifest: CaseManifestV1 | null | undefined,
  buildingId: string | undefined,
  title: string | undefined,
  opts?: { decor?: boolean }
): string {
  if (manifest && buildingId) {
    return resolveBuildingDisplayName(manifest, buildingId, { decor: opts?.decor });
  }
  const t = title?.trim() ?? "";
  if (t && looksLikeAssetSlugBuildingLabel(t)) {
    return compactAssetStyleBuildingLabel(t, buildingId ?? "");
  }
  return t || "Investigation";
}

export function zoneDisplayName(
  manifest: CaseManifestV1,
  zone: "downtown" | "maple_heights"
): string {
  const z = manifest.zones?.find((x) => x.id === zone);
  return z?.display_name ?? (zone === "downtown" ? "Downtown" : "Maple Heights");
}

export function poiKindSuffix(
  kind: "npc" | "building" | "transit",
  zoneLabel: string,
  opts?: { indoors?: boolean }
): string {
  if (kind === "transit") return "Transit";
  if (kind === "npc" && opts?.indoors) return `Inside · ${zoneLabel}`;
  return kind === "npc" ? `Witness · ${zoneLabel}` : `Building · ${zoneLabel}`;
}
